import { ColDef, ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import { Dispatch, SetStateAction } from 'react';

import Checkbox from '~/shared/components/Checkbox';
import Tag from '~/shared/components/Tag';
import { convertToGIB, formatBytes } from '~/shared/utils/formatNumber';
import { Volume } from '~/shared/types/api';

import {
  statusToTagVariantMap,
  getProvisionedUtilization,
  getDatafyUtilization,
} from '../common/helpers';

import { canBeDatafied, OptimisticSwitchType, optmisticSwitchingInterval } from './helpers';
import OptimisticSwitch from './OptimisticSwitch';
import VolumeIdCellRenderer from './VolumeIdCellRenderer';

export const getColumnDefs = ({
  onSelectAllRows,
  sortField,
  selectedVolumes,
  setSelectedVolumes,
  optimisticVolumeSwitches,
  setOptimisticVolumeSwitches,
}: {
  onSelectAllRows: VoidFunction;
  sortField: { field: string; dir: 'desc' | 'asc' };
  selectedVolumes: string[];
  setSelectedVolumes: Dispatch<SetStateAction<string[]>>;
  optimisticVolumeSwitches: OptimisticSwitchType;
  setOptimisticVolumeSwitches: Dispatch<SetStateAction<OptimisticSwitchType>>;
}): ColDef<Volume>[] => [
  {
    headerComponent: ({ api }: IHeaderParams<Volume>) => {
      const allRows: Volume[] = [];
      api.forEachNode((node) => (node?.data ? allRows.push(node.data) : null));
      const selectableRows = allRows.filter(canBeDatafied);

      return (
        <Checkbox
          onClick={onSelectAllRows}
          checked={
            selectedVolumes?.length > 0 && selectedVolumes?.length === selectableRows?.length
          }
          disabled={selectableRows?.length === 0}
          className="w-[16px] h-[16px] mb-[5px]"
        />
      );
    },
    colId: 'row-selector',
    cellRenderer: ({ data }: ICellRendererParams<Volume>) => (
      <Checkbox
        checked={selectedVolumes?.includes(data?.volumeId || '')}
        onChange={() =>
          setSelectedVolumes((prev) =>
            prev.includes(data?.volumeId || '')
              ? prev.filter((v) => v !== data?.volumeId || '')
              : [...prev, data?.volumeId || '']
          )
        }
        disabled={!canBeDatafied(data!)}
      />
    ),
    initialWidth: 20,
    pinned: 'left',
  },
  {
    headerName: 'Datafy it?',
    initialWidth: 87,
    minWidth: 87,
    sortable: false,
    cellRenderer: ({ data }: ICellRendererParams<Volume>) => (
      <OptimisticSwitch
        volume={data!}
        optimisticVolumeSwitches={optimisticVolumeSwitches}
        setOptimisticVolumeSwitches={setOptimisticVolumeSwitches}
      />
    ),
    pinned: 'left',
  },
  { field: 'volumeName', headerName: 'Name', initialWidth: 194, minWidth: 194 },
  {
    field: 'volumeId',
    headerName: 'Volume ID',
    sort: sortField?.field === 'volumeId' ? sortField?.dir : null,
    initialWidth: 194,
    minWidth: 194,
    cellRenderer: ({ data }: ICellRendererParams<Volume>) =>
      data?.volumeId ? <VolumeIdCellRenderer id={data.volumeId} /> : null,
  },
  {
    field: 'monitoring',
    headerName: 'Datafy Status',
    initialWidth: 146,
    minWidth: 146,
    sort: sortField?.field === 'monitoring' ? sortField?.dir : null,
    cellRenderer: ({ value, data }: ICellRendererParams<Volume>) => {
      if (!data || !value) {
        return null;
      }
      const progress = data.monitoring.endsWith('ing') ? data.statusProgress : undefined;

      const optimisticVolume = optimisticVolumeSwitches.find((v) => v.volId === data?.volumeId);

      const optimisticValue: Volume['monitoring'] =
        optimisticVolume && Date.now() - optimisticVolume?.date <= optmisticSwitchingInterval
          ? value === 'Managed'
            ? 'Unmigrating'
            : value === 'Monitored'
              ? 'Migrating'
              : value
          : value;

      return (
        <Tag
          text={optimisticValue}
          progress={progress}
          variant={statusToTagVariantMap[optimisticValue]}
        />
      );
    },
  },
  {
    field: 'diskSize',
    headerName: 'Size',
    sort: sortField?.field === 'diskSize' ? sortField?.dir : null,
    initialWidth: 78,
    minWidth: 78,
    valueFormatter: ({ value }) => formatBytes(value),
  },
  {
    field: 'diskUsed',
    headerName: 'Utilization',
    sort: sortField?.field === 'diskUsed' ? sortField?.dir : null,
    minWidth: 122,
    width: 122,
    valueFormatter: ({ data }) => getProvisionedUtilization(data),
  },
  {
    field: 'yearlyEstimatedSavings',
    headerName: 'Yearly Estimated Savings',
    sort: sortField?.field === 'yearlyEstimatedSavings' ? sortField?.dir : null,

    minWidth: 120,
    initialWidth: 120,
  },
  {
    field: 'datafyDiskSize',
    headerName: 'Datafy Size',
    sort: sortField?.field === 'datafyDiskSize' ? sortField?.dir : null,
    initialWidth: 94,
    minWidth: 94,
    valueFormatter: ({ value }) => convertToGIB(value),
  },
  {
    field: 'datafyDiskUsed',
    headerName: 'Datafy Utilization',
    sort: sortField?.field === 'datafyDiskUsed' ? sortField?.dir : null,

    minWidth: 122,
    width: 122,
    valueFormatter: ({ data }) => getDatafyUtilization(data),
  },
  {
    field: 'yearlyRealizedSavings',
    headerName: 'Yearly Realized Savings',
    sort: sortField?.field === 'yearlyRealizedSavings' ? sortField?.dir : null,

    minWidth: 120,
    initialWidth: 120,
  },

  {
    field: 'diskType',
    headerName: 'Type',
    sort: sortField?.field === 'diskType' ? sortField?.dir : null,
    initialWidth: 80,
    minWidth: 80,
  },
  {
    field: 'iops',
    headerName: 'IOPS',
    sort: sortField?.field === 'volumeIops' ? sortField?.dir : null,
    minWidth: 80,
    initialWidth: 80,
  },
  {
    field: 'throughput',
    headerName: 'Throughput',
    sort: sortField?.field === 'volumeThroughput' ? sortField?.dir : null,
    initialWidth: 124,
    minWidth: 124,
  },
  {
    field: 'awsState',
    headerName: 'AWS State',
    sort: sortField?.field === 'awsState' ? sortField?.dir : null,
    initialWidth: 124,
    minWidth: 124,
  },
  {
    field: 'awsStatus',
    headerName: 'AWS Status',
    sort: sortField?.field === 'awsStatus' ? sortField?.dir : null,
    initialWidth: 124,
    minWidth: 124,
  },
  {
    field: 'instanceId',
    headerName: 'Instance ID',
    sort: sortField?.field === 'instanceId' ? sortField?.dir : null,
    initialWidth: 204,
    minWidth: 204,
  },
  {
    field: 'availabilityZone',
    headerName: 'Availability Zone',
    sort: sortField?.field === 'availabilityZone' ? sortField?.dir : null,
    initialWidth: 154,
    minWidth: 154,
  },
  {
    field: 'region',
    headerName: 'Region',
    sort: sortField?.field === 'region' ? sortField?.dir : null,
    initialWidth: 124,
    minWidth: 124,
  },
  {
    field: 'clusterName',
    headerName: 'Cluster',
    sort: sortField?.field === 'clusterName' ? sortField?.dir : null,
    initialWidth: 100,
    minWidth: 100,
  },
  {
    field: 'nodeGroupName',
    headerName: 'Node Group',
    sort: sortField?.field === 'nodeGroupName' ? sortField?.dir : null,
    initialWidth: 390,
    minWidth: 390,
    cellRenderer: ({ value }: ICellRendererParams<Volume>) => (
      <span onClick={() => null} className="underline text-[#2151F5] cursor-pointer">
        {value}
      </span>
    ),
  },
  {
    field: 'osImage',
    headerName: 'OS Image',
    sort: sortField?.field === 'osImage' ? sortField?.dir : null,
    initialWidth: 158,
    minWidth: 158,
  },
  {
    field: 'isBootDisk',
    headerName: 'Boot Volume',
    sort: sortField?.field === 'isBootDisk' ? sortField?.dir : null,
    initialWidth: 130,
    minWidth: 130,
    cellRenderer: ({ data }: ICellRendererParams<Volume>) => (data?.isBootDisk ? 'Y' : 'N'),
  },
];
